import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spacer, Stack, Title } from '@kvdbil/components';
import CallSupport, {
  SupportHours,
  isOpenToday,
  supportDateFormat
} from './CallSupport';
import Link from '~/App/shared/components/Link';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import { useSelector, useDispatch } from 'react-redux';
import {
  hasForeignConnectionsSelector,
  isAuthenticated as isAuthenticatedSelector,
  memberCanBidSelector,
  session as sessionSelector
} from '~/App/shared/selectors/sessionSelector';
import { requestErrorHandler } from '~/helpers/notifyError';
import { getOpeningHours } from '~/helpers/orchestration/customerService';
import {
  objectViewDrawerSupportLastUpdatedSelector,
  objectViewDrawerSupportScheduleSelector
} from '~/App/shared/selectors/storeObject';
import { setCustomerSupportSchedule } from '~/App/shared/actions/objectViewDrawerActions';
import dayjs from 'dayjs';
import AuctionTips from '~/App/views/ObjectPage/components/BiddingView/components/Bidding/components/AuctionTips';
import { auctionTypeIsFixedPrice } from '~/helpers/buyCar';
import Restrictions from './Restrictions';
import { getRestrictionsInfo } from './helpers';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import BiddingViewAuthAndVerification from './Bidding/components/BiddingViewAuth/BiddingViewAuthAndVerification';
import { useAccountVerificationStatus } from './Bidding/hooks/useAccountVerificationStatus';
import { useGA4ObjectEvent } from '~/App/shared/hooks/useGA4ObjectEvent';
import {
  BodyTextSkeleton,
  CustomHeightSkeleton,
  SkeletonContainer
} from '~/App/shared/components/Skeletons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

interface Props {
  auction: ProcessedAuction;
}

function FixedPrice({ auction }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [authInUse, setAuthInUse] = useState(false);
  const session = useSelector(sessionSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const isFixedPriceObject = auctionTypeIsFixedPrice(auction.auctionType);
  const hasForeignConnections = useSelector(hasForeignConnectionsSelector);
  const memberCanReserve = useSelector(memberCanBidSelector);
  const { accountVerificationStatus } = useAccountVerificationStatus();
  const isAuthAndVerificationComplete = !accountVerificationStatus;

  useGA4ObjectEvent('view_reserve_car_page', auction);

  const restrictionsInfo = getRestrictionsInfo(
    session?.member,
    true,
    auction,
    hasForeignConnections,
    t
  );

  const schedule = useSelector(objectViewDrawerSupportScheduleSelector);
  const scheduleLastUpdate = useSelector(
    objectViewDrawerSupportLastUpdatedSelector
  );
  const scheduleNeedsUpdate =
    scheduleLastUpdate &&
    dayjs().isAfter(dayjs(scheduleLastUpdate).add(1, 'day'));
  const [supportHours, setSupportHours] = useState<SupportHours | null>(
    schedule.length > 0 ? isOpenToday(schedule) : null
  );

  const mediationFeePerson = isFixedPriceObject
    ? auction.mediationFee
    : auction.biddingFee;
  const mediationFeeCorporate = isFixedPriceObject
    ? auction.mediationFeeCorporate
    : auction.biddingFeeCorporate;
  const mediationFeeAmount =
    session.member.memberType === 'PERSON'
      ? mediationFeePerson
      : mediationFeeCorporate;
  const parsedMediationFeeAmount = parseInt(String(mediationFeeAmount));

  useEffect(() => {
    const currentDate = dayjs().format(supportDateFormat);

    if ((schedule.length === 0 || scheduleNeedsUpdate) && !supportHours) {
      getOpeningHours(currentDate, 7)
        .then(data => {
          dispatch(setCustomerSupportSchedule(data));
          const result = isOpenToday(data);
          setSupportHours(result);
        })
        .catch((error: unknown) => {
          requestErrorHandler(error);
        });
    }
  }, [schedule, scheduleNeedsUpdate, dispatch, supportHours]);

  const supportSection = useMemo(() => {
    if (supportHours === null) {
      return (
        <SkeletonContainer>
          <BodyTextSkeleton />
          <CustomHeightSkeleton height={4} />
          <BodyTextSkeleton />
        </SkeletonContainer>
      );
    }

    return (
      <CallSupport
        title={t(`...or call a sales person and we'll help you.`)}
        showSupportHours
        hours={supportHours}
      />
    );
  }, [supportHours, t]);

  const renderButton = () => {
    return (
      <Link to={`/ROUTES.CHECKOUT/ROUTES.CHECKOUT_START/${auction.id}/BUY_NOW`}>
        <Button
          isDisabled={
            (!isAuthAndVerificationComplete && !memberCanReserve) ||
            restrictionsInfo.showRestrictionsBox
          }
          fullWidth
          color="info"
          colorShade="dark"
          size="large"
        >
          {t('Go to the checkout')}
        </Button>
      </Link>
    );
  };

  return (
    <Container>
      <Stack align={'center'}>
        {!authInUse && (
          <div>
            <Title>{t('Buy the car at a fixed price')}</Title>
            {isAuthenticated && !isNaN(parsedMediationFeeAmount) && (
              <AuctionTips
                feeAmount={parsedMediationFeeAmount}
                isBiddingObject={false}
                hasDeductibleVat={auction.processObject.deductibleVat}
              />
            )}
          </div>
        )}

        {restrictionsInfo?.showRestrictionsBox && isAuthenticated && (
          <Restrictions text={restrictionsInfo?.text} />
        )}

        <BiddingViewAuthAndVerification
          setAuthInUse={setAuthInUse}
          isBiddingObject={false}
        />

        {!authInUse && (
          <>
            {renderButton()}
            <Spacer height={0.5} />
            {supportSection}
          </>
        )}
      </Stack>
    </Container>
  );
}

export default FixedPrice;
